import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { productsService, ordersService, pricingService } from '../../services';
import { PricingAddon, PricingPlan } from '../../services/pricing.service';
import { ProductCategory } from '../../constants/productCategory';
import { Product } from '../../types/product.type';
import { useTranslation } from 'react-i18next';
import { VMConfig } from '../../types/vm.type';
import ProgressSteps from './components/ProgressSteps';
import Step1VM from './steps/Step1VM';
import Step2VMPricing from './steps/Step2VMPricing';
import Step3VMPricing from './steps/Step3VMPricing';

const CreateVMPricing: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<VMConfig>({
    name: '',
    region: '',
    instanceType: '',
    os: '',
    storage: 60,
    backups: false,
    monitoring: true,
    needsPublicIP: true,
    publicIPType: 'dynamic',
    allowedIPs: '',
    exposedPorts: '',
    ipExpiration: '24h',
    understandsRisk: false,
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [selectedOSType, setSelectedOSType] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const hasFetchedProducts = useRef(false);

  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [selectedVariantCode, setSelectedVariantCode] = useState<string | null>(null);
  const [addons, setAddons] = useState<PricingAddon[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingAddons, setIsLoadingAddons] = useState(false);

  useEffect(() => {
    if (hasFetchedProducts.current) return;
    hasFetchedProducts.current = true;

    const fetchProducts = async () => {
      try {
        const response = await productsService.getProductsByCategory(ProductCategory.VM);
        const productsArray = Array.isArray(response?.products) ? response.products : [];
        setProducts(productsArray);
      } catch (error) {
        console.error('Error fetching VM products:', error);
        setProducts([]);
        hasFetchedProducts.current = false;
      }
    };

    fetchProducts();
  }, []);

  const getOSIcon = (osfamily: string, productImages?: string[]): { type: 'image' | 'emoji'; value: string } => {
    if (productImages && productImages.length > 0) {
      return { type: 'image', value: productImages[0] };
    }

    const logoMap: Record<string, string> = {
      'amazon-linux': '/images/amazon linux.png',
      'ubuntu-22.04': '/images/ubuntu.png',
      'ubuntu-20.04': '/images/ubuntu.png',
      ubuntu: '/images/ubuntu.png',
      rhel: '/images/linux.png',
      redhat: '/images/linux.png',
      'red hat': '/images/linux.png',
      linux: '/images/linux.png',
      debian: '/images/debian.png',
      centos: '/images/linux.png',
      suse: '/images/suse.png',
      sles: '/images/suse.png',
      opensuse: '/images/suse.png',
      windows: '/images/windows.png',
      macos:
        'https://www.apple.com/ac/globalnav/7/en_US/images/be15095f-5a20-57d0-ad14-c6d1742a92cf/globalnav_apple_image__b5er5ng3xqq6_large.svg',
    };

    for (const [key, logoUrl] of Object.entries(logoMap)) {
      if (osfamily.toLowerCase().includes(key.toLowerCase())) {
        return { type: 'image', value: logoUrl };
      }
    }

    return { type: 'emoji', value: '💻' };
  };

  const operatingSystems = useMemo(() => {
    if (!Array.isArray(products) || products.length === 0) return [];

    let osProducts = products.filter((product) => {
      const osFamily = product.osFamily;
      return osFamily && typeof osFamily === 'string';
    });

    if (selectedOSType) {
      osProducts = osProducts.filter(
        (product) => product.osType?.toLowerCase() === selectedOSType.toLowerCase()
      );
    }

    return osProducts.map((product) => {
      const osFamily = product.osFamily as string;
      const iconData = getOSIcon(osFamily, product.images);
      return {
        id: product.id,
        name: product.name,
        description: product.description,
        osFamily,
        osType: product.osType,
        icon: iconData.value,
        isImage: iconData.type === 'image',
        product,
      };
    });
  }, [products, selectedOSType]);

  const availableOSTypes = useMemo(() => {
    if (!Array.isArray(products) || products.length === 0) return [];

    const osProducts = products.filter((product) => product.osFamily && product.osType);
    const uniqueTypes = Array.from(
      new Set(
        osProducts
          .map((p) => p.osType?.toLowerCase())
          .filter((type): type is string => Boolean(type))
      )
    );
    return uniqueTypes;
  }, [products]);

  const selectedProduct = useMemo(
    () => products.find((product) => product.id === config.os) || null,
    [products, config.os]
  );

  const basePlans = useMemo(() => {
    const grouped = new Map<string, PricingPlan[]>();
    plans.forEach((plan) => {
      if (!grouped.has(plan.planCode)) grouped.set(plan.planCode, []);
      grouped.get(plan.planCode)!.push(plan);
    });

    return Array.from(grouped.values())
      .map((variants) => {
        const standard = variants.find((variant) => variant.variantCode === 'STANDARD');
        if (standard) return standard;
        const noVariant = variants.find((variant) => !variant.variantCode);
        if (noVariant) return noVariant;
        return [...variants].sort(
          (a, b) => (a.variantOrder ?? Number.MAX_SAFE_INTEGER) - (b.variantOrder ?? Number.MAX_SAFE_INTEGER)
        )[0];
      })
      .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER));
  }, [plans]);

  const planVariants = useMemo(() => {
    if (!selectedPlanCode) return [];
    return plans
      .filter((plan) => plan.planCode === selectedPlanCode)
      .sort((a, b) => (a.variantOrder ?? Number.MAX_SAFE_INTEGER) - (b.variantOrder ?? Number.MAX_SAFE_INTEGER));
  }, [plans, selectedPlanCode]);

  const selectedPlan = useMemo(() => {
    if (!selectedPlanCode) return null;

    const selectedVariant = plans.find(
      (plan) => plan.planCode === selectedPlanCode && (plan.variantCode || 'STANDARD') === (selectedVariantCode || 'STANDARD')
    );
    if (selectedVariant) return selectedVariant;

    const standard = planVariants.find((variant) => variant.variantCode === 'STANDARD');
    if (standard) return standard;

    return planVariants[0] || null;
  }, [plans, planVariants, selectedPlanCode, selectedVariantCode]);

  const selectedAddons = useMemo(
    () => addons.filter((addon) => addon.addonCode === 'BACKUP_DAILY' && config.backups),
    [addons, config.backups]
  );

  useEffect(() => {
    if (config.os && selectedOSType) {
      const selectedOS = operatingSystems.find((os) => os.id === config.os);
      if (!selectedOS || selectedOS.osType?.toLowerCase() !== selectedOSType.toLowerCase()) {
        setConfig((prev) => ({ ...prev, os: '' }));
      }
    }
  }, [selectedOSType, config.os, operatingSystems]);

  useEffect(() => {
    const osType = selectedProduct?.osType;
    const osFamily = selectedProduct?.osFamily;
    if (!osType) {
      setPlans([]);
      setSelectedPlanCode(null);
      setSelectedVariantCode(null);
      setAddons([]);
      return;
    }

    const loadPlans = async () => {
      setIsLoadingPlans(true);
      setSelectedPlanCode(null);
      setSelectedVariantCode(null);
      setAddons([]);
      setConfig((prev) => ({ ...prev, instanceType: '' }));

      try {
        const pricingPlans = await pricingService.getPlans('EC2', osType, osFamily);
        setPlans(pricingPlans);
        if (pricingPlans.length > 0) {
          const grouped = new Map<string, PricingPlan[]>();
          pricingPlans.forEach((plan) => {
            if (!grouped.has(plan.planCode)) grouped.set(plan.planCode, []);
            grouped.get(plan.planCode)!.push(plan);
          });

          const firstPlan = Array.from(grouped.values())
            .map((variants) => variants.find((variant) => variant.variantCode === 'STANDARD') || variants[0])
            .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER))[0];

          if (!firstPlan) return;
          setSelectedPlanCode(firstPlan.planCode);
          setSelectedVariantCode(firstPlan.variantCode || 'STANDARD');
          setConfig((prev) => ({
            ...prev,
            instanceType: firstPlan.providerConfiguration?.instanceType || firstPlan.planCode,
            storage: firstPlan.includedResources?.storageGb || prev.storage,
          }));
        }
      } catch (error) {
        console.error('Error fetching pricing plans:', error);
        setPlans([]);
      } finally {
        setIsLoadingPlans(false);
      }
    };

    loadPlans();
  }, [selectedProduct?.id, selectedProduct?.osType, selectedProduct?.osFamily]);

  useEffect(() => {
    if (!selectedPlan) {
      return;
    }

    setConfig((prev) => ({
      ...prev,
      instanceType: selectedPlan.providerConfiguration?.instanceType || selectedPlan.planCode || prev.instanceType,
      storage: selectedPlan.includedResources?.storageGb || prev.storage,
    }));
  }, [selectedPlan]);

  useEffect(() => {
    const osType = selectedProduct?.osType;
    const osFamily = selectedProduct?.osFamily;
    if (!selectedPlanCode || !osType) {
      setAddons([]);
      return;
    }

    const loadAddons = async () => {
      setIsLoadingAddons(true);
      setAddons([]);

      try {
        const addonsResponse = await pricingService.getPlanAddons('EC2', osType, selectedPlanCode, osFamily);
        setAddons(addonsResponse);

        const hasBackupAddon = addonsResponse.some((addon) => addon.addonCode === 'BACKUP_DAILY');
        if (!hasBackupAddon) {
          setConfig((prev) => ({ ...prev, backups: false }));
        }
      } catch (error) {
        console.error('Error fetching plan addons:', error);
        setAddons([]);
      } finally {
        setIsLoadingAddons(false);
      }
    };

    loadAddons();
  }, [selectedPlanCode, selectedProduct?.osType, selectedProduct?.osFamily]);

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleCreate = async () => {
    if (config.needsPublicIP && !config.publicIPType) {
      alert(t('virtualMachines.create.validation.publicIPRequired'));
      return;
    }
    if (!config.os || !config.instanceType || !selectedPlanCode || !selectedProduct?.osType || !selectedPlan) {
      console.error('Missing required configuration');
      return;
    }

    setIsCreating(true);
    try {
      if (!selectedProduct) {
        console.error('Selected product not found');
        setIsCreating(false);
        return;
      }

      const orderData = {
        productId: selectedProduct.id,
        quantity: 1,
        metadata: {
          type: 'COMPUTE',
          pricingSelection: {
            typeFamily: 'EC2',
            osType: selectedProduct.osType?.toUpperCase(),
            osFamily: selectedProduct.osFamily,
            planCode: selectedPlanCode,
            variantCode: selectedPlan.variantCode || 'STANDARD',
            variantName: selectedPlan.variantName || selectedPlan.displayName || selectedPlan.planName,
            addons: selectedAddons.map((addon) => addon.addonCode),
          },
          vmConfig: {
            name: config.name,
            os: config.os,
            region: config.region,
            ...(config.needsPublicIP && {
              publicIP: { type: config.publicIPType },
            }),
          },
        },
      };

      const createdOrder = await ordersService.create(orderData);
      if (!createdOrder?.id) throw new Error('La API no devolvió el ID de la orden');
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      alert(t('common.loading'));
      setIsCreating(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return Boolean(config.name && config.os);
      case 2:
        return Boolean(selectedPlanCode && selectedVariantCode && config.instanceType);
      case 3:
        return true;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1VM
            config={config}
            setConfig={setConfig}
            operatingSystems={operatingSystems}
            selectedOSType={selectedOSType}
            setSelectedOSType={setSelectedOSType}
            availableOSTypes={availableOSTypes}
            isLoading={products.length === 0}
          />
        );
      case 2:
        return (
          <Step2VMPricing
            config={config}
            setConfig={setConfig}
            plans={basePlans}
            selectedPlanCode={selectedPlanCode}
            onSelectPlan={(planCode) => {
              setSelectedPlanCode(planCode);
              const variants = plans
                .filter((plan) => plan.planCode === planCode)
                .sort((a, b) => (a.variantOrder ?? Number.MAX_SAFE_INTEGER) - (b.variantOrder ?? Number.MAX_SAFE_INTEGER));
              const plan = variants.find((variant) => variant.variantCode === 'STANDARD') || variants[0];
              if (plan) {
                setSelectedVariantCode(plan.variantCode || 'STANDARD');
                setConfig((prev) => ({
                  ...prev,
                  instanceType: plan.providerConfiguration?.instanceType || plan.planCode,
                  storage: plan.includedResources?.storageGb || prev.storage,
                  backups: false,
                }));
              }
            }}
            planVariants={planVariants}
            selectedVariantCode={selectedVariantCode}
            onSelectVariant={(variantCode) => {
              setSelectedVariantCode(variantCode);
              const variant = plans.find(
                (plan) => plan.planCode === selectedPlanCode && (plan.variantCode || 'STANDARD') === variantCode
              );
              if (variant) {
                setConfig((prev) => ({
                  ...prev,
                  instanceType: variant.providerConfiguration?.instanceType || variant.planCode,
                  storage: variant.includedResources?.storageGb || prev.storage,
                  backups: false,
                }));
              }
            }}
            selectedPlan={selectedPlan}
            addons={addons}
            isLoadingPlans={isLoadingPlans}
            isLoadingAddons={isLoadingAddons}
          />
        );
      case 3:
        return (
          <Step3VMPricing
            config={config}
            operatingSystems={operatingSystems}
            selectedPlan={selectedPlan}
            selectedAddons={selectedAddons}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6">
      <div className="mb-8">
        <button
          onClick={() => navigate('/virtual-machines')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftOutlined />
          <span className="text-sm font-medium">{t('common.back')}</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('virtualMachines.create.pricing.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">{t('virtualMachines.list.subtitle')}</p>
      </div>

      <ProgressSteps currentStep={step} />

      <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {renderStep()}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('virtualMachines.create.actions.back')}
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/virtual-machines')}
            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('virtualMachines.create.actions.cancel')}
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('virtualMachines.create.actions.next')}
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating
                ? t('virtualMachines.create.actions.creating')
                : t('virtualMachines.create.actions.create')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateVMPricing;
